import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const VarianceCharts = ({ data }) => {
  const [projectStages, setProjectStages] = useState([]);

  // Fetch project stages on component mount
  useEffect(() => {
    const fetchProjectStages = async () => {
      try {
        const response = await axios.get('/api/project-stages', { withCredentials: true });
        console.log('Fetched project stages:', response.data);
        setProjectStages(response.data);
      } catch (error) {
        console.error('Error fetching project stages:', error);
        // Fallback to default stages if fetch fails
        setProjectStages([
          { id: 1, stage_number: 1, name: 'Inception' },
          { id: 3, stage_number: 2, name: 'Concept and Viability' },
          { id: 5, stage_number: 3, name: 'Design Development' },
          { id: 7, stage_number: 4, name: 'Documentation and Procurement' },
          { id: 9, stage_number: 5, name: 'Construction' },
          { id: 11, stage_number: 6, name: 'Close-out' }
        ]);
      }
    };

    fetchProjectStages();
  }, []);

  // Debug logging to understand data structure
  console.log('VarianceCharts - Full data object:', data);
  console.log('VarianceCharts - Users:', data?.users);
  console.log('VarianceCharts - Projects:', data?.projects);
  console.log('VarianceCharts - ProjectUsers:', data?.projectUsers);
  console.log('VarianceCharts - Project Stages from API:', projectStages);

  // LDM brand colors
  const ldmColors = {
    primary: 'rgba(45, 58, 69, 0.8)',    // #2D3A45 with opacity
    secondary: 'rgba(255, 87, 34, 0.8)', // #FF5722 with opacity
    accent1: 'rgba(61, 74, 85, 0.8)',    // #3D4A55 with opacity
    accent2: 'rgba(255, 122, 80, 0.8)',  // #FF7A50 with opacity
    allowable: 'rgba(76, 175, 80, 0.8)', // #4CAF50 with opacity - green for allowable hours
    stage1: 'rgba(156, 39, 176, 0.8)',   // Purple for Stage 1
    stage2: 'rgba(103, 58, 183, 0.8)',   // Deep Purple for Stage 2
    stage3: 'rgba(63, 81, 181, 0.8)',    // Indigo for Stage 3
    stage4: 'rgba(33, 150, 243, 0.8)',   // Blue for Stage 4
    stage5: 'rgba(0, 188, 212, 0.8)',    // Cyan for Stage 5
    stage6: 'rgba(76, 175, 80, 0.8)',    // Green for Stage 6
  };

  // Prepare data for charts
  const projectData = {
    labels: data?.projects?.map(p => p.name) || [],
    datasets: [
      {
        label: 'Estimated Hours',
        data: data?.projects?.map(p => p.planned_hours) || [],
        backgroundColor: ldmColors.primary,
        borderColor: '#2D3A45',
        borderWidth: 1,
      },
      {
        label: 'Logged Hours',
        data: data?.projects?.map(p => p.actual_hours) || [],
        backgroundColor: ldmColors.secondary,
        borderColor: '#FF5722',
        borderWidth: 1,
      },
      {
        label: 'Allowable Hours',
        data: data?.projects?.map(p => p.allowable_hours || 0) || [],
        backgroundColor: ldmColors.allowable,
        borderColor: '#4CAF50',
        borderWidth: 1,
      },
    ],
  };

  const userData = {
    labels: data?.users?.map(u => u.name) || [],
    datasets: [
      {
        label: 'Estimated Hours',
        data: data?.users?.map(u => u.planned_hours) || [],
        backgroundColor: ldmColors.accent1,
        borderColor: '#3D4A55',
        borderWidth: 1,
      },
      {
        label: 'Logged Hours',
        data: data?.users?.map(u => u.actual_hours) || [],
        backgroundColor: ldmColors.accent2,
        borderColor: '#FF7A50',
        borderWidth: 1,
      },
      {
        label: 'Allowable Hours',
        data: data?.users?.map(u => {
          // Calculate user's total allowable hours from all assigned projects
          if (!data?.projects || !data?.projectUsers) return 0;
          
          // Find all projects this user is assigned to
          const userProjectIds = data.projectUsers
            .filter(pu => pu.user_id === u.id)
            .map(pu => pu.project_id);
          
          // Sum up allowable hours from those projects (same as Project Hours chart)
          const totalAllowableHours = data.projects
            .filter(p => userProjectIds.includes(p.id))
            .reduce((sum, p) => sum + (p.allowable_hours || 0), 0);
          
          return totalAllowableHours;
        }) || [],
        backgroundColor: ldmColors.allowable,
        borderColor: '#4CAF50',
        borderWidth: 1,
      },
    ],
  };

  // Prepare stage hours per user data
  const prepareStageUserData = () => {
    if (!data?.users || !data?.plannedHoursEntries || !projectStages.length) {
      return { labels: [], datasets: [] };
    }

    console.log('Stage User Data - Available data:', {
      users: data.users?.length,
      plannedHoursEntries: data.plannedHoursEntries?.length,
      sampleEntry: data.plannedHoursEntries?.[0],
      projectStages: projectStages.length
    });

    // Map the actual project stages from database with proper colors
    const stages = projectStages.map((stage, index) => {
      const colorMap = [
        ldmColors.stage1, // Stage 1
        ldmColors.stage2, // Stage 2  
        ldmColors.stage3, // Stage 3
        ldmColors.stage4, // Stage 4
        ldmColors.stage5, // Stage 5
        ldmColors.stage6, // Stage 6
      ];
      
      return {
        id: stage.id, // Use actual database ID (1, 3, 5, 7, 9, 11)
        name: stage.stage_number > 6 ? stage.name : `Stage ${stage.stage_number}: ${stage.name}`, // Use stage_number for display
        color: colorMap[stage.stage_number - 1] || ldmColors.primary // Map by stage_number
      };
    });

    const userLabels = data.users.map(u => u.name);
    
    console.log('🎨 Stage mapping for chart:', stages.map(s => ({
      dbId: s.id,
      displayName: s.name,
      color: s.color
    })));
    
    // Create datasets for each stage
    const datasets = stages.map(stage => {
      const stageData = data.users.map(user => {
        // Find all WPS entries for this user in this stage
        const userStageEntries = data.plannedHoursEntries.filter(entry => 
          entry.user_id === user.id && entry.project_stage_id === stage.id
        );
        
        // Sum up hours from WPS entries for this user in this stage
        const totalHours = userStageEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
        
        console.log(`User ${user.name} - Stage ${stage.id}: ${totalHours} hours from ${userStageEntries.length} entries`);
        
        return totalHours;
      });

      return {
        label: stage.name,
        data: stageData,
        backgroundColor: stage.color,
        borderColor: stage.color.replace('0.8', '1'),
        borderWidth: 1,
      };
    });

    // Always show all stages for consistency, even if they have zero hours
    // This provides better UX and avoids confusion about missing stages
    console.log('All stage datasets (including zero data):', datasets.length);

    return {
      labels: userLabels,
      datasets: datasets
    };
  };

  const stageUserData = prepareStageUserData();

  // Debug the calculated data
  console.log('VarianceCharts - Project data:', projectData);
  console.log('VarianceCharts - User data:', userData);
  console.log('VarianceCharts - Stage-User data:', stageUserData);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Make columns narrower
    barPercentage: 0.6, // Reduced from 0.8
    categoryPercentage: 0.7, // Reduced from 0.8
    plugins: {
      legend: { position: 'top' },
      // Disable datalabels for Project Hours and User Workload charts
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw || 0;

            // Calculate variance only for the "Logged Hours" dataset
            let variance = '';
            if (context.datasetIndex === 1) { // Logged Hours dataset
              const estimatedHours = context.chart.data.datasets[0].data[context.dataIndex] || 0; // Get Estimated Hours
              const loggedHours = value;
              const variancePercent = estimatedHours !== 0
                ? ((loggedHours - estimatedHours) / estimatedHours * 100).toFixed(1)
                : 0;

              // Add +/- sign for better readability
              const sign = variancePercent > 0 ? '+' : '';
              variance = ` (${sign}${variancePercent}% variance)`;
            }

            // Add percentage of allowable hours for allowable hours dataset
            let allowableInfo = '';
            if (context.datasetIndex === 2) { // Allowable Hours dataset
              const loggedHours = context.chart.data.datasets[1].data[context.dataIndex] || 0;
              const allowableHours = value;
              if (allowableHours > 0) {
                const percentUsed = ((loggedHours / allowableHours) * 100).toFixed(1);
                allowableInfo = ` (${percentUsed}% used)`;
              }
            }

            return `${label}: ${value.toFixed(1)}h${variance}${allowableInfo}`;
          },
          title: (tooltipItems) => {
            return `Construction Project: ${tooltipItems[0].label}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Hours' },
        max: function(scale) {
          // Find the maximum value across all datasets
          const chart = scale.chart;
          const data = chart.data;
          let maxValue = 0;
          
          if (data && data.datasets) {
            data.datasets.forEach(dataset => {
              if (dataset.data) {
                dataset.data.forEach(value => {
                  if (value > maxValue) {
                    maxValue = value;
                  }
                });
              }
            });
          }
          
          // Add 50 hours above the highest bar for Project Hours and User Workload charts
          return Math.ceil(maxValue) + 50;
        }
      },
      x: {
        title: { display: true, text: 'Projects' }, // Always show "Projects" for the Project Hours chart
        ticks: {
          // Use a 45-degree angle for better readability with long labels
          maxRotation: 45,
          minRotation: 45,
          // Add padding to prevent overlap
          padding: 15,
          // Ensure labels don't overlap
          autoSkip: false,
          // Allow more space for labels
          font: {
            size: 12
          },
          // Align text to end (right) for angled labels
          align: 'end',
          // Truncate long labels but show full name in tooltip
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            // If label is too long, truncate it
            if (label && label.length > 20) {
              return label.substring(0, 17) + '...';
            }
            return label;
          }
        }
      }
    },
    // Add more space at the bottom for labels
    layout: {
      padding: {
        bottom: 50
      }
    }
  };

  // Options for User Workload chart
  const userOptions = {
    ...options,
    plugins: {
      ...options.plugins,
      tooltip: {
        callbacks: {
          ...options.plugins.tooltip.callbacks,
          title: (tooltipItems) => {
            return `User: ${tooltipItems[0].label}`;
          }
        }
      }
    },
    scales: {
      ...options.scales,
      x: {
        ...options.scales.x,
        title: { display: true, text: 'Users' }
      }
    }
  };

  // Special options for stage-user chart
  const stageUserOptions = {
    ...options,
    plugins: {
      ...options.plugins,
      legend: { 
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          },
          // Allow legend to wrap to multiple lines if needed
          usePointStyle: false
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: ${value.toFixed(1)}h`;
          },
          title: (tooltipItems) => `User: ${tooltipItems[0].label}`
        }
      },
      // Add data labels plugin to show values on bars
      datalabels: {
        color: '#2D3A45',
        font: {
          size: 10,
          weight: 'bold'
        },
        anchor: 'end',
        align: 'top',
        offset: 4,
        formatter: function(value, context) {
          // Only show if value is greater than 0
          if (!value || value <= 0) {
            return null;
          }
          
          // Show stage number and value
          const stageLabel = context.dataset?.label || '';
          // Extract stage number from label (e.g., "Stage 1: Inception" -> "S1")
          const stageMatch = stageLabel.match(/Stage (\d+):/);
          const stageNumber = stageMatch ? `S${stageMatch[1]}` : '';
          return stageNumber + '\n' + value.toFixed(1) + 'h';
        }
      }
    },
    scales: {
      ...options.scales,
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Hours' },
        max: function(scale) {
          // Find the maximum value across all datasets
          const chart = scale.chart;
          const data = chart.data;
          let maxValue = 0;
          
          if (data && data.datasets) {
            data.datasets.forEach(dataset => {
              if (dataset.data) {
                dataset.data.forEach(value => {
                  if (value > maxValue) {
                    maxValue = value;
                  }
                });
              }
            });
          }
          
          // Add 10 hours above the highest bar
          return Math.ceil(maxValue) + 10;
        }
      },
      x: {
        ...options.scales.x,
        title: { display: true, text: 'Users' }
      }
    }
  };

  return (
    <Box>
      {/* First row: Project Hours and User Workload */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Paper sx={{
          p: 3,
          borderRadius: '16px', // More rounded corners
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)', // More prominent shadow
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)', // Even more prominent shadow on hover
          }
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', fontWeight: 500 }}>Project Hours</Typography>
          <Typography variant="body2" sx={{ color: '#5A6A75' }} gutterBottom>
            Comparison of estimated, logged, and allowable hours for projects with recent WPS activity
          </Typography>
          <Box sx={{ height: '500px', mt: 2 }}>
            <Bar options={options} data={projectData} />
          </Box>
        </Paper>

        <Paper sx={{
          p: 3,
          borderRadius: '16px', // More rounded corners
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)', // More prominent shadow
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)', // Even more prominent shadow on hover
          }
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', fontWeight: 500 }}>User Workload - WPS Activity in Last 30 Days</Typography>
          <Typography variant="body2" sx={{ color: '#5A6A75' }} gutterBottom>
            Distribution of estimated, logged, and allowable hours for users assigned to projects with recent WPS activity
          </Typography>
          <Box sx={{ height: '500px', mt: 2 }}>
            <Bar options={userOptions} data={userData} />
          </Box>
        </Paper>
      </Box>

      {/* Second row: Stage Hours per User (full width) */}
      <Paper sx={{
        p: 3,
        borderRadius: '16px',
        boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
        }
      }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', fontWeight: 500 }}>
          Stage Hours per User
        </Typography>
        <Typography variant="body2" sx={{ color: '#5A6A75' }} gutterBottom>
          Distribution of planned hours across project stages for each user
        </Typography>
        <Box sx={{ height: '500px', mt: 2 }}>
          <Bar options={stageUserOptions} data={stageUserData} />
        </Box>
      </Paper>
    </Box>
  );
};

export default VarianceCharts;